import React from 'react'
import useLatestRef from 'hooks/useLatestRef'

export const INVALID_DATE = 'INVALID_DATE'

const defaultDateState = {
  day: null,
  month: null,
  year: null,
  date: null
}

const getDaysInMonth = ({ year, month }) => new Date(year, month + 1, 0).getDate()

const refreshDayIfNecessary = (state) => getDaysInMonth(state) < state.day && (state.day = null)

const getDate = ({ year, month, day }) => {
  if (year === null || month === null || day === null) return INVALID_DATE
  return new Date(year, month, day)
}

const getStateFromDate = (date) =>
  date instanceof Date && {
    day: date.getDate(),
    month: date.getMonth(),
    year: date.getFullYear(),
    date
  }

const reducer = (state, action) => {
  const { type, payload } = action
  const nextState = { ...state }
  switch (type) {
    case 'CHANGE_DAY':
      nextState.day = payload
      nextState.date = getDate(nextState)
      return nextState
    case 'CHANGE_MONTH':
      nextState.month = payload
      refreshDayIfNecessary(nextState)
      nextState.date = getDate(nextState)
      return nextState
    case 'CHANGE_YEAR':
      nextState.year = payload
      refreshDayIfNecessary(nextState)
      nextState.date = getDate(nextState)
      return nextState
    case 'CHANGE_DATE':
      return getStateFromDate(payload) || (
        payload === null
          ? defaultDateState
          : { ...nextState, date: INVALID_DATE }
      )
    case 'RESET':
      return defaultDateState
    default:
      return state
  }
}

const useDatePicker = (value = null, onChange) => {
  const [state, dispatch] = React.useReducer(
    reducer,
    value,
    (date) => getStateFromDate(date) || defaultDateState
  )

  const setDay = React.useCallback(day => dispatch({ type: 'CHANGE_DAY', payload: day }), [])
  const setMonth = React.useCallback(month => dispatch({ type: 'CHANGE_MONTH', payload: month }), [])
  const setYear = React.useCallback(year => dispatch({ type: 'CHANGE_YEAR', payload: year }), [])
  const setDate = React.useCallback(date => dispatch({ type: 'CHANGE_DATE', payload: date }), [])
  const reset = React.useCallback(() => dispatch({ type: 'RESET' }), [])

  const daysInMonth = React.useMemo(() => getDaysInMonth(state), [state])

  const onChangeRef = useLatestRef(onChange)

  const isMountedRef = React.useRef(false)

  React.useEffect(() => {
    if (isMountedRef.current && typeof onChangeRef.current === 'function') {
      onChangeRef.current(state.date)
    }
    isMountedRef.current = true
  }, [onChangeRef, state.date])

  React.useEffect(() => {
    setDate(value)
  }, [value, setDate])

  return { state, setDay, setMonth, setYear, setDate, reset, daysInMonth }
}

export default useDatePicker
